import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { useQuestionBanks, useQuestions, type Question, type QuestionBank } from "@/app/questionQueries";
import { useMe } from "@/app/session";
import { count, plural } from "@/lib/plural";
import { EmptyState, SkeletonRows } from "@/ui/states";
import { NetworkError } from "./route/bits";
import { Inline } from "./step/Inline";

/** Банк вопросов с собеседований: карточки банков → вопросы банка по разделам,
 *  частота «спрашивали N раз» (`[×K]` базы знаний), ответы видит только staff. */
export function QuestionsPage({ bank }: { bank?: string }) {
  const me = useMe();
  const banks = useQuestionBanks();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const staff = me.data ? me.data.role !== "student" : false;

  if (banks.status === "pending") {
    return (
      <Shell nav user={user} meta="банк вопросов">
        <SkeletonRows rows={6} />
        <TabBar />
      </Shell>
    );
  }
  if (banks.status === "error") {
    return (
      <Shell nav user={user} meta="банк вопросов">
        <NetworkError retry={() => void banks.refetch()} />
        <TabBar />
      </Shell>
    );
  }
  const list = banks.data;
  const asked = list.reduce((n, b) => n + b.asked, 0);
  const items = list.reduce((n, b) => n + b.items, 0);
  const current = bank ? list.find((b) => b.name === bank) : undefined;

  return (
    <Shell nav user={user} meta="банк вопросов">
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-6 px-4 pb-20 pt-7 md:px-6">
        <header>
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">{current ? <Link to="/questions" className="text-accdim no-underline hover:underline">банк вопросов</Link> : "студент · подготовка к собеседованиям"}</div>
          <h1 className="m-0 font-mono text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-ink md:text-[34px]">{current ? current.name : "Банк вопросов"}</h1>
          <p className="mb-0 mt-3 max-w-[680px] text-[13.5px] leading-[1.55] text-ink2 text-pretty">
            {current
              ? bankLine(current, plural)
              : list.length === 0
                ? ""
                : `${count(list.length, "банк", "банка", "банков")} · ${count(items, "вопрос", "вопроса", "вопросов")}${asked > 0 ? ` · с настоящих собеседований — спрашивали ${count(asked, "раз", "раза", "раз")}` : ""}. Банк шага открывается из карточки «Готов к переходу».`}
          </p>
        </header>
        {bank && !current && (
          <EmptyState where="банк вопросов" title={`Банка «${bank}» нет`} body="В базе знаний на него ссылается гейт шага, но файла с вопросами ещё нет. Другие банки — ниже." primary={{ label: "все банки", href: "/questions" }} />
        )}
        {current ? <BankQuestions bank={current.name} staff={staff} /> : list.length === 0 ? <NoBanks staff={staff} /> : <BankCards banks={list} />}
      </div>
      <TabBar />
    </Shell>
  );
}

function bankLine(b: QuestionBank, pl: typeof plural): string {
  const items = `${b.items} ${pl(b.items, "вопрос", "вопроса", "вопросов")}`;
  return b.asked > 0 ? `${items} · спрашивали ${b.asked} ${pl(b.asked, "раз", "раза", "раз")} на настоящих собеседованиях` : `${items} · для самопроверки перед гейтом`;
}

function NoBanks({ staff }: { staff: boolean }) {
  return (
    <EmptyState
      where="банк вопросов"
      title="Банк вопросов пока пуст"
      body={staff ? "Вопросы появятся после импорта базы знаний (QA и QAInterviews) или из CSV во вкладке «гейт» шага в конструкторе курса." : "Вопросы к гейтам появятся, когда менторы наполнят банк. Пока — «Проверь себя» на шагах маршрута."}
      primary={staff ? { label: "в конструктор курса", href: "/admin/course" } : { label: "на мой маршрут", href: "/" }}
    />
  );
}

function BankCards({ banks }: { banks: QuestionBank[] }) {
  return (
    <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3" data-testid="banks">
      {banks.map((b) => (
        <li key={b.name}>
          <Link to="/questions" search={{ bank: b.name }} className="flex h-full flex-col gap-2 rounded-md border border-bd bg-card p-4 no-underline transition-colors hover:border-bd2 hover:bg-card2" data-testid="bank-card">
            <span className="font-mono text-[14px] font-semibold text-ink">{b.name}</span>
            <span className="font-mono text-[11.5px] text-ink2">{count(b.items, "вопрос", "вопроса", "вопросов")}</span>
            {b.asked > 0 && <span className="font-mono text-[11px] text-accdim">спрашивали {count(b.asked, "раз", "раза", "раз")}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function BankQuestions({ bank, staff }: { bank: string; staff: boolean }) {
  const q = useQuestions(bank);
  if (q.status === "pending") return <SkeletonRows rows={8} />;
  if (q.status === "error") return <NetworkError retry={() => void q.refetch()} />;
  const groups: { section: string; items: Question[] }[] = [];
  for (const it of q.data) {
    const section = it.tags[0] ?? "";
    const g = groups.find((x) => x.section === section);
    if (g) g.items.push(it);
    else groups.push({ section, items: [it] });
  }
  if (q.data.length === 0) {
    return <EmptyState where="банк вопросов" title="В этом банке пока нет вопросов" body="Банк создан, но вопросы ещё не загружены." primary={{ label: "все банки", href: "/questions" }} />;
  }
  let n = 0;
  return (
    <div className="flex flex-col gap-6" data-testid="questions">
      {groups.map((g) => (
        <section key={g.section || "—"}>
          {g.section && <h2 className="m-0 mb-2.5 font-mono text-[12px] font-semibold uppercase tracking-[.06em] text-mut">{g.section}</h2>}
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {g.items.map((it) => {
              n += 1;
              return (
                <li key={it.id} className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-baseline gap-3 rounded-md border border-bd bg-card px-4 py-3" data-testid="question">
                  <span className="font-mono text-[12px] text-mut">{n}</span>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-[14px] leading-[1.5] text-ink text-pretty">
                      <Inline text={it.question} />
                    </span>
                    {staff && it.answer !== undefined && it.answer !== "" && (
                      <details className="text-[13px] leading-[1.5] text-ink2">
                        <summary className="cursor-pointer font-mono text-[11.5px] text-accdim">ответ · видят только менторы</summary>
                        <p className="m-0 mt-1.5 text-pretty">
                          <Inline text={it.answer} />
                        </p>
                      </details>
                    )}
                  </div>
                  <span className="font-mono text-[11px] text-accdim" title={it.asked_count > 0 ? `спрашивали ${count(it.asked_count, "раз", "раза", "раз")}` : "из «Проверь себя»"}>
                    {it.asked_count > 0 ? `×${it.asked_count}` : ""}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
